<template>
  <dialog :open="isOpen" class="modal modal-bottom sm:modal-middle">
    <div class="modal-box" :class="modalSizeClass">
      <h3 class="font-bold text-lg mb-4">Inspect File: {{ file?.fileName }}</h3>

      <div class="py-4 overflow-y-auto bg-base-200 rounded p-4" :class="contentHeightClass">
        <div v-if="file?.isLoading === true" class="flex justify-center items-center h-48">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
        <div v-else-if="file?.error" class="flex justify-center items-center h-48">
          <p class="text-error">{{ file.error }}</p>
        </div>
        <template v-else-if="file !== null">
        
          <div v-if="file.fileType === 'websiteurl' || file.fileType === 'website'">
            <p><strong>Type:</strong> {{ file.fileType === 'websiteurl' ? 'Website URL' : 'Scraped Website' }}</p>
            <p v-if="safeWebsiteUrlHref !== null"><strong>URL:</strong> <a :href="safeWebsiteUrlHref" target="_blank" rel="noopener noreferrer" class="link link-primary">{{ file.url }}</a></p>
            <p v-else><strong>URL:</strong> <span class="text-gray-500">{{ file.url }}</span></p>
            <p v-if="file.maxPages !== null && file.maxPages !== undefined"><strong>Max Pages Scraped:</strong> {{ file.maxPages }}</p>
            <p class="mt-2 text-sm text-gray-500">(Content is processed and stored in the vector database)</p>
          </div>

         
          <div v-else-if="file.fileType?.toLowerCase() === 'text' || file.fileType?.toLowerCase() === 'txt' || file.fileType?.toLowerCase() === 'fineTuneAnswer'">
            <p><strong>Type:</strong> {{ getDisplayFileType(file.fileType) }}</p>
            <pre class="whitespace-pre-wrap text-sm mt-2" v-html="safeHighlightedTextHtml !== '' ? safeHighlightedTextHtml : 'Content not directly available for preview.'"></pre>
             <p v-if="file.content === null || file.content === undefined" class="mt-2 text-sm text-gray-500">(Content might be processed or only base64 data is stored)</p>
          </div>

          
          <div v-else>
             <p><strong>Type:</strong> {{ getDisplayFileType(file.fileType) }}</p>

             
             <div v-if="file.fileType === 'pdf' && pdfSource !== null" class="mt-2 border rounded h-full">
               <VuePdfEmbed ref="pdfViewer" :source="pdfSource" :page="currentPdfPage" />
             </div>

 
             <div v-else-if="file.fileType === 'docx'" class="mt-2 border rounded p-2 bg-white text-black">
               <div v-if="isLoadingDocx === true" class="flex justify-center items-center h-20">
                 <span class="loading loading-spinner loading-md"></span>
               </div>
               <div v-else v-html="safeDocxHtml" class="docx-preview"></div>
             </div>

             <div v-else class="mt-2">
               <p class="text-sm text-gray-500">Preview not available for this file type ({{ file.fileType }}).</p>
               <p class="text-sm text-gray-500">File content is processed for the knowledge base.</p>
             </div>

     
             <button v-if="file.fileData !== null && file.fileData !== undefined" @click="downloadFile" class="btn btn-sm btn-secondary mt-4">Download File</button>
          </div>
        </template>
        <div v-else>
          <p>No file selected.</p>
        </div>
      </div>

      <div class="modal-action">
        <button class="btn" @click="$emit('close')">Close</button>
      </div>
    </div>
     <div class="modal-backdrop" @click.stop></div>
  </dialog>
</template>

<script setup>
import { computed, ref, watch, onUnmounted } from 'vue';
import { useToast } from 'vue-toastification';
import VuePdfEmbed from 'vue-pdf-embed';
import * as mammoth from 'mammoth';
import axios from '@/api/axiosInstance';
import { useRoute } from 'vue-router';
const route = useRoute();

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  file: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['close']);
const toast = useToast();

function sanitizeUrlForHref(value) {
  if (value === undefined || value === null) {
    return null;
  }
  const raw = String(value).trim();
  if (raw.length === 0) {
    return null;
  }
  let urlObj;
  try {
    urlObj = new URL(raw, window.location.origin);
  } catch (e) {
    return null;
  }
  const proto = typeof urlObj.protocol === 'string' ? urlObj.protocol.toLowerCase() : '';
  if (proto === 'http:' || proto === 'https:') {
    return urlObj.href;
  }
  return null;
}

function sanitizeHtml(rawHtml) {
  const input = typeof rawHtml === 'string' ? rawHtml : (rawHtml === null || rawHtml === undefined ? '' : String(rawHtml));
  if (input.trim().length === 0) {
    return '';
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString('<div>' + input + '</div>', 'text/html');
  const container = doc.body.firstChild;
  if (container === undefined || container === null) {
    return '';
  }
  const allowed = new Set(['div', 'span', 'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'img']);
  function clean(node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      if (allowed.has(tag) === false) {
        const parent = node.parentNode;
        while (node.firstChild !== null) {
          parent.insertBefore(node.firstChild, node);
        }
        parent.removeChild(node);
        return;
      }
      if (tag === 'a') {
        const href = node.getAttribute('href') !== null ? node.getAttribute('href') : '';
        if (/^(https?:|mailto:|tel:|#)/i.test(href) === false) {
          node.removeAttribute('href');
        } else {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
        }
      }
      if (tag === 'img') {
        const src = node.getAttribute('src') !== null ? node.getAttribute('src') : '';
        const safeDataImg = /^data:image\/(?:png|jpe?g|gif|webp);base64,[a-z0-9+/=]+$/i.test(src);
        if (safeDataImg === false) {
          node.removeAttribute('src');
        }
      }
      if (node.hasAttribute('style') === true) {
        const style = node.getAttribute('style') !== null ? node.getAttribute('style') : '';
        const parts = style.split(';').map(function(p){ return p.trim(); }).filter(function(p){ return p.length > 0; });
        const safeParts = parts.filter(function(p){ const l = p.toLowerCase(); return l.indexOf('background-color') === 0 || l.indexOf('color') === 0; });
        if (safeParts.length > 0) {
          node.setAttribute('style', safeParts.join(';'));
        } else {
          node.removeAttribute('style');
        }
      }
      Array.from(node.attributes).forEach(function(attr) {
        if (/^on/i.test(attr.name) === true) {
          node.removeAttribute(attr.name);
        }
        if (attr.name.toLowerCase() === 'class') {
          node.removeAttribute(attr.name);
        }
      });
    }
    let child = node.firstChild;
    while (child !== null) {
      const next = child.nextSibling;
      clean(child);
      child = next;
    }
  }
  clean(container);
  return container.innerHTML.trim();
}

const safeWebsiteUrlHref = computed(() => {
  const url = props.file !== null && props.file !== undefined ? props.file.url : null;
  return sanitizeUrlForHref(url);
});


const docxHtml = ref('');
const isLoadingDocx = ref(false);
const currentPdfPage = ref(null);

const getDisplayFileType = (type) => {
    if (type === null) return 'File';
    switch(type.toLowerCase()) {
        case 'text':
        case 'txt': return 'Text File';
        case 'pdf': return 'PDF Document';
        case 'docx': return 'Word Document';
        case 'websiteurl': return 'Website URL';
        case 'website': return 'Scraped Website';
        case 'finetuneanswer': return 'Fine-Tuned Answer';
        default: return type.toUpperCase();
    }
}


const pdfSource = computed(() => {
    if (props.file?.fileType === 'pdf' && props.file?.fileData !== undefined && props.file?.fileData !== null) {
        return `data:application/pdf;base64,${props.file.fileData}`;
    }
    return null;
});


function base64ToArrayBuffer(base64) {
    const binary_string = window.atob(base64);
    const len = binary_string.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
}

function getFileExtension(fileName) {
  if (fileName === undefined || fileName === null) {
    return '';
  }
  const name = String(fileName);
  const index = name.lastIndexOf('.');
  if (index < 0 || index === name.length - 1) {
    return '';
  }
  return name.slice(index + 1).toLowerCase();
}

function getMimeTypeFromExtension(extension) {
  const ext = typeof extension === 'string' ? extension.toLowerCase() : '';
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  if (ext === 'doc') return 'application/msword';
  if (ext === 'txt' || ext === 'text') return 'text/plain';
  if (ext === 'csv') return 'text/csv';
  if (ext === 'json') return 'application/json';
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'svg') return 'image/svg+xml';
  if (ext === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (ext === 'xls') return 'application/vnd.ms-excel';
  if (ext === 'pptx') return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
  if (ext === 'zip') return 'application/zip';
  if (ext === 'rar') return 'application/vnd.rar';
  if (ext === '7z') return 'application/x-7z-compressed';
  if (ext === 'mp3') return 'audio/mpeg';
  if (ext === 'wav') return 'audio/wav';
  if (ext === 'mp4') return 'video/mp4';
  if (ext === 'mov') return 'video/quicktime';
  if (ext === 'html' || ext === 'htm') return 'text/html';
  return 'application/octet-stream';
}

function resolveMimeType(file, dataUrlMime) {
  if (dataUrlMime !== undefined && dataUrlMime !== null && dataUrlMime !== '') {
    return dataUrlMime;
  }
  const fileType = file !== undefined && file !== null ? file.fileType : null;
  if (fileType !== undefined && fileType !== null && String(fileType).trim() !== '') {
    return getMimeTypeFromExtension(String(fileType).trim());
  }
  const ext = getFileExtension(file !== undefined && file !== null ? file.fileName : null);
  if (ext !== '') {
    return getMimeTypeFromExtension(ext);
  }
  return 'application/octet-stream';
}

async function renderDocx(base64Data) {
    if (base64Data === undefined || base64Data === null) return;
    isLoadingDocx.value = true;
    docxHtml.value = ''; 
    try {
        const arrayBuffer = base64ToArrayBuffer(base64Data);
        const result = await mammoth.convertToHtml({ arrayBuffer: arrayBuffer });
        let htmlContent = result.value;
        if (props.file?.highlight !== undefined && props.file.highlight !== null && props.file.highlight.length > 0) {
          const pattern = new RegExp(escapeRegExp(props.file.highlight), 'gi');
          htmlContent = htmlContent.replace(pattern, '<span style="background-color:yellow">$&</span>');
        }
        docxHtml.value = sanitizeHtml(htmlContent);
    } catch (error) {
        console.error('Error converting DOCX to HTML:', error);
        docxHtml.value = sanitizeHtml('<p>Error loading document preview.</p>');
        toast.error('Failed to load DOCX preview.');
    } finally {
        isLoadingDocx.value = false;
    }
}

watch(() => props.file, async (newFile) => {
    if (newFile?.fileType === 'docx' && newFile?.fileData !== undefined && newFile?.fileData !== null) {
        renderDocx(newFile.fileData);
    } else {
        
        docxHtml.value = '';
        isLoadingDocx.value = false;
    }
    if (newFile?.fileType === 'pdf') {
      if (newFile.page !== undefined && newFile.page !== null) {
        currentPdfPage.value = newFile.page;
      }
    }
    
    if (newFile?.fileType?.toLowerCase() === 'text' || newFile?.fileType?.toLowerCase() === 'txt') {
        if (newFile.content === undefined || newFile.content === null) {
            try {
                const response = await axios.get(
                    `/agents/${route.query.edit}/files/${encodeURIComponent(newFile.fileName)}`,
                    { responseType: 'text' }
                );
                newFile.content = response.data;
            } catch (error) {
                console.error('Error loading text file content:', error);
                newFile.error = 'Failed to load text file content.';
            }
        }
    }
}, { immediate: true }); 

const downloadFile = () => {
  if (props.file === undefined || props.file === null || props.file.fileData === undefined || props.file.fileData === null) {
    console.error("No file or file data available for download.");
    return;
  }

  const data = props.file.fileData;
  let blob = null;
  if ((data instanceof Blob) === true) {
    blob = data;
  } else if ((data instanceof ArrayBuffer) === true) {
    const mimeType = resolveMimeType(props.file, null);
    blob = new Blob([data], { type: mimeType });
  } else if (ArrayBuffer.isView(data) === true) {
    const mimeType = resolveMimeType(props.file, null);
    blob = new Blob([data], { type: mimeType });
  } else if (typeof data === 'string') {
    let base64 = data;
    let dataUrlMime = '';
    const match = /^data:([^;]+);base64,(.+)$/i.exec(data);
    if (match !== null && match[1] !== undefined && match[2] !== undefined) {
      dataUrlMime = match[1];
      base64 = match[2];
    }
    const cleanBase64 = base64.replace(/\s/g, '');
    if (cleanBase64.length === 0) {
      console.error("No data available for download.");
      return;
    }
    const arrayBuffer = base64ToArrayBuffer(cleanBase64);
    const mimeType = resolveMimeType(props.file, dataUrlMime);
    blob = new Blob([arrayBuffer], { type: mimeType });
  } else {
    console.error("Unsupported file data type for download.");
    return;
  }

  if (blob === null) {
    console.error("No file data available for download.");
    return;
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', props.file.fileName !== undefined && props.file.fileName !== null && props.file.fileName !== '' ? props.file.fileName : 'download');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};


const modalSizeClass = computed(() => {
  return props.file?.fileType === 'pdf' ? 'max-w-5xl' : '';
});


const contentHeightClass = computed(() => {
   return props.file?.fileType === 'pdf' ? 'max-h-[90vh]' : 'max-h-96';
});


let originalBodyOverflow = ''; 

watch(() => props.isOpen, (newVal) => {
  if (newVal === true) {

    originalBodyOverflow = document.body.style.overflowY; 
    document.body.style.overflowY = 'hidden'; 
  } else {

    document.body.style.overflowY = originalBodyOverflow; 
  }
});

onUnmounted(() => {

  if (props.isOpen === true) {
      document.body.style.overflowY = originalBodyOverflow;
  }
});

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const highlightedText = computed(() => {
  if (props.file?.content === undefined || props.file.content === null) {
    return '';
  }
  if (props.file?.highlight === undefined || props.file.highlight === null || props.file.highlight.length === 0) {
    return props.file.content;
  }
  const pattern = new RegExp(escapeRegExp(props.file.highlight), 'gi');
  return props.file.content.replace(pattern, '<span style="background-color:yellow">$&</span>');
});

const safeHighlightedTextHtml = computed(() => {
  const raw = highlightedText.value;
  if (typeof raw !== 'string') {
    return '';
  }
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return '';
  }
  return sanitizeHtml(trimmed);
});

const safeDocxHtml = computed(() => {
  const raw = docxHtml.value;
  if (typeof raw !== 'string') {
    return '';
  }
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return '';
  }
  return sanitizeHtml(trimmed);
});

const pdfViewer = ref(null);

</script>

<style scoped>

pre {
  font-family: inherit; 
}


.docx-preview ::v-deep(table) {
  word-break: break-word;
  width: 100% !important; 
  table-layout: fixed;
}
.docx-preview ::v-deep(td),
.docx-preview ::v-deep(th) {
   overflow-wrap: break-word; 
}
</style>